import { useAuthActions } from "@convex-dev/auth/react";
import { GithubLogo, Robot } from "@phosphor-icons/react";
import * as React from "react";
import { Button } from "~/components/ui/button";
import { Wordmark } from "~/components/Wordmark";

/**
 * Full-screen login page shown to unauthenticated visitors.
 *
 * Only one provider for now — GitHub OAuth, restricted server-side to
 * members of the @hyrdrocks org (see convex/auth.ts). If someone outside
 * the org signs in, GitHub will succeed but our profile callback will
 * reject and they'll see the error message below.
 */
export function SignInScreen() {
  const { signIn } = useAuthActions();
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function onSignIn() {
    setSubmitting(true);
    setError(null);
    try {
      await signIn("github");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-background px-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <Robot size={26} weight="duotone" />
          </div>
          <Wordmark className="text-2xl" />
          <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
            Full-Stack Engineer Agent
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h1 className="text-base font-semibold">Sign in</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Alfred is restricted to members of the{" "}
            <a
              href="https://github.com/hyrdrocks"
              target="_blank"
              rel="noreferrer"
              className="font-mono text-foreground hover:underline"
            >
              hyrdrocks
            </a>{" "}
            GitHub organization. Sign in with your GitHub account to continue.
          </p>

          <Button
            type="button"
            onClick={onSignIn}
            disabled={submitting}
            className="mt-5 w-full"
            size="lg"
          >
            <GithubLogo size={18} weight="fill" />
            {submitting ? "Redirecting to GitHub…" : "Continue with GitHub"}
          </Button>

          {error ? (
            <p className="mt-3 rounded-lg border border-[color:var(--status-error)]/40 bg-[color:var(--status-error)]/10 p-2 text-xs text-[color:var(--status-error)]">
              {error}
            </p>
          ) : null}
        </div>

        <p className="mt-4 text-center text-[11px] text-muted-foreground">
          Not yet a member of @hyrdrocks? Ask an admin to add you, then refresh.
        </p>
      </div>
    </div>
  );
}
